import {
  type DocumentVersion,
  getDocumentById,
  invalidateDocumentListData,
  prepareDocumentForSave,
  type Role,
} from "@prismicio/editor-fields"
import { getDateDiffString } from "@prismicio/editor-support/Date"
import { isRequestError } from "@prismicio/editor-support/Http"
import {
  Button,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  Text,
  Tooltip,
  useAlert,
  useTour,
} from "@prismicio/editor-ui"
import { useQueryClient } from "@tanstack/react-query"
import { useState, useTransition } from "react"
import { z } from "zod"

import { env } from "../../../app/env"
import { baseUrl, type DocumentVersionStatus } from "../../../app/services/core"
import { getReleaseId } from "../../../app/services/release"
import { invalidateRepositoryTags } from "../../../app/services/repository"
import { useRole } from "../../../app/services/user"
import {
  getBaseVersion,
  revalidateDocumentMeta,
  saveDocument,
} from "../../documentData"
import { useExistingDocument } from "../../DocumentProvider"
import {
  useExistingDocumentParams,
  useGoToDocumentVersion,
  useRepositoryReleases,
} from "../../documentRouting"
import { findDocumentVersion } from "../../findDocumentVersion"
import { validateUID } from "../validateUID"
import { BlockNavigationOnUnsavedChangesPrompt } from "./BlockNavigationOnUnsavedChangesPrompt"
import { actionAlert, type PendingActionProps } from "./HeaderButtons"

interface SaveButtonProps extends PendingActionProps {}

export function SaveButton(props: SaveButtonProps) {
  const { pendingAction } = props

  const {
    disabled,
    action,
    roleCanSave,
    tooltip,
    conflictingVersion,
    clearVersionConflict,
  } = useSaveButton(props)

  const { documentId } = useExistingDocumentParams()

  return (
    <>
      <Tooltip
        visible={!roleCanSave}
        title={tooltip.title}
        content={tooltip.content}
      >
        <Button
          size="large"
          color="grey"
          loading={pendingAction === "save"}
          disabled={disabled}
          hotkeys="mod+s"
          onClick={() => action()}
          data-demo-id="save-button"
        >
          Save
        </Button>
      </Tooltip>

      <BlockNavigationOnUnsavedChangesPrompt
        // enable the prompt if the save button is not disabled
        // this means that there have been changes to the document
        enabled={!disabled}
        action={useSaveButton}
      />

      <VersionConflictDialog
        conflictingVersion={conflictingVersion}
        onDismiss={clearVersionConflict}
        onSave={() => action({ useConflictAsBaseVersion: true })}
        onDiscardChanges={() => {
          clearVersionConflict()
          // Force fetch the most recent document - discards local draft
          void revalidateDocumentMeta(documentId)
        }}
      />
    </>
  )
}

export function useSaveButton(props: SaveButtonProps) {
  const { pendingAction, onPendingActionChange } = props

  const { content, draft, edited, data, locale } = useExistingDocument()
  const [conflictingVersion, setConflictingVersion] =
    useState<DocumentVersion | null>(null)

  const { meta, version, customType } = data

  const [isUpdating, startTransition] = useTransition()
  const goToDocumentVersion = useGoToDocumentVersion()
  const { showAlert } = useAlert()

  const { role } = useRole(locale)

  const queryClient = useQueryClient()

  const releases = useRepositoryReleases()

  const isScheduledForRelease = releases.some(
    (release) => release.id === getReleaseId(data.version) && release.timestamp,
  )

  const roleCanSave = canSave(role, isScheduledForRelease)

  const tour = useTour()

  const disabled =
    tour.step?.id !== "save-your-content" &&
    tour.step?.id !== "reorder-your-slices-2" &&
    (!edited || Boolean(pendingAction) || isUpdating || !roleCanSave)

  async function save(args?: { useConflictAsBaseVersion?: boolean }) {
    const { useConflictAsBaseVersion = false } = args || {}
    onPendingActionChange("save")

    // Note: Standard Save uses the current version. Throws bad_version_error error if not the newest, and shows the conflict dialog.
    // Pass the useConflictAsBaseVersion=true to use conflictingVersion as the base version, forcing the save to happen (Save anyway).
    const baseVersion =
      useConflictAsBaseVersion && conflictingVersion
        ? conflictingVersion
        : getBaseVersion(version, meta.versions)
    // Clean up Version Conflict so everything returns to normal state, the user will see the loading spinner in the "Save" button.
    clearVersionConflict()

    const releaseId = getReleaseId(baseVersion)

    // Note: we want to prepare the document before validating the UID to avoid bad content+model combinations.
    // This code ordering would not matter if we implemented:
    // https://linear.app/prismic/issue/PBD-1529/sanitize-document-based-on-custom-type-as-early-as-possible
    const preparedDocument = prepareDocumentForSave(customType, content)

    const isValidUID = await validateUID({
      content: preparedDocument,
      documentId: meta.id,
      languageId: meta.language.id,
      customType,
      showAlert,
      pendingAction: "save",
      onRetry: save,
    })

    if (!isValidUID) {
      onPendingActionChange(undefined)
      return
    }

    try {
      const newVersion = await saveDocument({
        id: meta.id,
        baseVersionId: baseVersion.version_id,
        releaseId,
        documentTags: draft.documentTags,
        content: preparedDocument,
        status: baseVersion.status,
      })

      if (
        tour.step?.id === "save-your-content" ||
        tour.step?.id === "reorder-your-slices-2"
      )
        tour.next()

      void invalidateDocumentListData(queryClient)

      // Invalidate tags as some might have been created in the process
      invalidateRepositoryTags()

      startTransition(() =>
        goToDocumentVersion({
          status: newVersion.status,
          releaseId,
          preventScrollReset: true,
        }),
      )
    } catch (err) {
      // If the error is bad_base_version, show the Version conflict dialog
      if (isBadBaseVersionError(err)) {
        await fetchConflictingVersion({
          documentId: meta.id,
          releaseId,
          status: baseVersion.status,
        })
        return
      }

      actionAlert({ action: "save", showAlert, onRetry: save })
    } finally {
      onPendingActionChange(undefined)
    }
  }

  async function fetchConflictingVersion(args: {
    documentId: string
    releaseId: string | undefined
    status: DocumentVersionStatus | undefined
  }) {
    const { documentId, releaseId, status } = args
    try {
      const meta = await getDocumentById(
        baseUrl,
        env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
        documentId,
      )
      // Get the most recent version of the current document
      const version = findDocumentVersion({
        meta,
        documentId,
        releaseId,
        status,
        archivedVersionId: undefined,
      })
      setConflictingVersion(version)
    } catch {
      actionAlert({ action: "save", showAlert, onRetry: save })
    }
  }

  function clearVersionConflict() {
    setConflictingVersion(null)
  }

  return {
    disabled,
    action: save,
    roleCanSave,
    tooltip: getTooltip(isScheduledForRelease),
    conflictingVersion,
    clearVersionConflict,
  }
}

function canSave(role: Role, isScheduledForRelease: boolean) {
  const savePermission: Record<Role, boolean> = {
    SuperUser: true,
    Owner: true,
    Administrator: true,
    Publisher: true,
    Writer: !isScheduledForRelease,
    Contributor: false,
  }

  return savePermission[role]
}

function getTooltip(isScheduledForRelease: boolean) {
  if (isScheduledForRelease)
    return {
      title: "You can't update documents scheduled for release",
      content:
        "Only users with the role of publisher or administrator can update documents scheduled for release.",
    }

  return {
    title: "You can't update documents",
    content:
      "Only users with the role of publisher or administrator can update documents.",
  }
}

interface ConflictChangesDialogProps {
  conflictingVersion: DocumentVersion | null
  onSave: () => void
  onDismiss: () => void
  onDiscardChanges?: () => void
}

function VersionConflictDialog(props: ConflictChangesDialogProps) {
  const { conflictingVersion, onSave, onDiscardChanges, onDismiss } = props

  const lastModifiedDate = conflictingVersion
    ? getDateDiffString(conflictingVersion.last_modified_date)
    : null

  return (
    <Dialog
      open={conflictingVersion !== null}
      size="small"
      onOpenChange={onDismiss}
    >
      <DialogHeader icon="alert" title="Conflicting changes" />

      <DialogDescription asChild>
        <DialogContent padding={16}>
          <Text>
            This document has been modified by{" "}
            <Text variant="bold">
              {conflictingVersion?.author.first_name} {lastModifiedDate}
            </Text>
            . By saving your changes, you may override the other version. Do you
            want to proceed?
          </Text>
        </DialogContent>
      </DialogDescription>

      <DialogActions>
        <DialogCancelButton onClick={onDismiss}>
          Keep editing
        </DialogCancelButton>
        <DialogActionButton onClick={onDiscardChanges} color="grey">
          Discard my changes
        </DialogActionButton>
        <DialogActionButton onClick={onSave} color="grey">
          Save anyway
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

function isBadBaseVersionError(error: unknown) {
  if (!isRequestError(error)) return false
  if (error.detail.type !== "http") return false

  const badVersionBodySchema = z.object({
    error: z.literal("bad_base_version"),
  })

  return badVersionBodySchema.safeParse(error.detail.body).success
}
